import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Flex, Menu, MenuButton, MenuGroup, MenuItem, MenuList, Text, useOutsideClick } from '@chakra-ui/react';
import { memo, useEffect, useRef, useState } from 'react';
import { Error24, Success24 } from '../../assets/icons';
import ChevronDown from '../../assets/icons/ChevronDown';
import ChevronUp from '../../assets/icons/ChevronUp';
import { formatDataTestId } from '../../utils/formatters';
import Icon from '../Icon';
import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
function Dropdown(props) {
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'default' : _props$variant,
    _props$options = props.options,
    options = _props$options === void 0 ? [] : _props$options,
    CustomContent = props.children,
    dropdownStyles = props.dropdownStyles,
    _props$isOpenMenu = props.isOpenMenu,
    isOpenMenu = _props$isOpenMenu === void 0 ? false : _props$isOpenMenu,
    matchWidth = props.matchWidth,
    dataTestId = props.dataTestId,
    hasError = props.hasError,
    disabled = props.disabled,
    showStatusIcon = props.showStatusIcon,
    selectedId = props.selectedId,
    onDisplayContent = props.onDisplayContent,
    onBlur = props.onBlur,
    className = props.className,
    tabbingAccessibility = props.tabbingAccessibility,
    focusableRefs = props.focusableRefs,
    _props$accessibilityR = props.accessibilityRole,
    accessibilityRole = _props$accessibilityR === void 0 ? 'menu' : _props$accessibilityR;
  var baseDataTestId = 'DropdownComp';
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    selectedValue = _useState2[0],
    setSelectedValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpen = _useState4[0],
    setIsOpen = _useState4[1];
  var isCombobox = accessibilityRole === 'combobox';
  var displayIcon = showStatusIcon && !disabled;
  var isValid = !hasError && selectedValue;
  var ref = useRef(null);
  useEffect(() => {
    if (isOpenMenu !== isOpen) {
      setIsOpen(isOpenMenu);
    }
  }, [isOpenMenu]);
  useOutsideClick({
    ref: ref,
    handler: () => {
      setIsOpen(false);
      onDisplayContent === null || onDisplayContent === void 0 || onDisplayContent(false);
      if (isOpen) {
        onBlur === null || onBlur === void 0 || onBlur();
      }
    }
  });
  useEffect(() => {
    if (selectedId) {
      var o = getOptionById(selectedId);
      setSelectedValue(o);
    }
  }, [selectedId, options]);
  return _jsxs(_Fragment, {
    children: [!showStatusIcon && renderDropdownField(), showStatusIcon && _jsxs(Flex, _objectSpread(_objectSpread({}, dropdownWithStatusIconWrapperStyle), {}, {
      children: [renderDropdownField(), renderStatusIcons()]
    }))]
  });
  function renderStatusIcons() {
    return _jsxs(Box, {
      children: [displayIcon && hasError && _jsx(Box, _objectSpread(_objectSpread({}, errorIconStyle), {}, {
        "data-testid": "inputIconError",
        children: _jsx(Error24, {})
      })), displayIcon && isValid && _jsx(Box, _objectSpread(_objectSpread({}, errorIconStyle), {}, {
        "data-testid": "inputIconSuccess",
        children: _jsx(Success24, {})
      }))]
    });
  }
  function renderDropdownField() {
    return _jsx(Menu, _objectSpread(_objectSpread({
      gutter: 1,
      isOpen: isOpen
    }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuStyles), {}, {
      matchWidth: matchWidth,
      "data-testid": formatDataTestId(baseDataTestId, 'Container'),
      children: _jsxs(Box, _objectSpread(_objectSpread({
        w: "full",
        pos: "relative"
      }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.wrapperStyles), {}, {
        "data-testid": formatDataTestId(baseDataTestId, 'Wrapper'),
        className: className,
        children: [props.label && _jsx(Text, _objectSpread(_objectSpread(_objectSpread({
          "data-testid": formatDataTestId(baseDataTestId, 'Label'),
          pos: "absolute",
          color: getLabelColor(isOpen)
        }, labelStyles), dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.labelTextStyles), {}, {
          children: props.label
        })), _jsxs(Box, _objectSpread(_objectSpread({
          ref: ref,
          as: "span"
        }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuButtonWrapperStyles), {}, {
          children: [_jsx(MenuButton, _objectSpread(_objectSpread({
            onClick: () => {
              setIsOpen(!isOpen);
              onDisplayContent === null || onDisplayContent === void 0 || onDisplayContent(!isOpen);
            },
            ref: el => {
              if (el && isCombobox) {
                el.setAttribute('role', 'combobox');
                el.setAttribute('aria-haspopup', 'listbox');
                var ariaLabel = props.label || props.placeholder;
                if (ariaLabel) el.setAttribute('aria-label', ariaLabel);
              }
              if (!el || !focusableRefs) return;
              if (!focusableRefs.current.includes(el)) {
                focusableRefs.current = [...focusableRefs.current, el];
              }
            },
            onKeyDown: event => {
              if (tabbingAccessibility && focusableRefs) {
                tabbingAccessibility(event, focusableRefs, isOpen);
              }
            },
            _hover: _objectSpread(_objectSpread({
              color: 'darkGrey2'
            }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.errorHoverMenuButtonStyles), isOpen ? {
              border: '2px solid var(--chakra-colors-primary)',
              borderColor: 'var(--chakra-colors-primary)',
              borderWidth: '2px'
            } : {
              border: '1px solid var(--chakra-colors-darkGrey1)',
              borderColor: 'var(--chakra-colors-darkGrey1)',
              borderWidth: '1px'
            }),
            _focus: _objectSpread({}, !isOpen && hasError && {
              border: '2px solid var(--chakra-colors-error)'
            })
          }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuButtonStyles), {}, {
            _focusVisible: {
              border: "2px solid var(--chakra-colors-".concat(hasError ? 'error' : 'primary', ")")
            },
            disabled: props.disabled,
            type: "button",
            "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-menuButton")),
            children: _jsxs(Flex, {
              align: "center",
              whiteSpace: "nowrap",
              "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-insideMenuButton")),
              children: [!(selectedValue !== null && selectedValue !== void 0 && selectedValue.icon) && props.icon && _jsx(Box, {
                "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-menuButtonFlag")),
                mr: "md",
                children: props.icon
              }), selectedValue ? _jsx(Flex, {
                w: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                alignItems: "center",
                "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-menuButtonText")),
                children: renderDropdownOption(selectedValue, true)
              }) : props.placeholder && _jsx(Box, _objectSpread(_objectSpread(_objectSpread({
                as: "span"
              }, placeholderStyle), dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuButtonTextStyles), {}, {
                "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-menuButtonOptionsText")),
                children: props.placeholder
              })), !props.skipChevron && _jsx(Icon, {
                ml: "auto",
                "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-menuButtonOptionsEndChevron")),
                svg: isOpen ? _jsx(ChevronUp, {}) : _jsx(ChevronDown, {})
              })]
            })
          })), CustomContent ? _jsx(CustomContent, {
            isOpen: isOpen,
            onChange: props.onChange,
            setSelectedValue: setSelectedValue,
            setIsOpen: setIsOpen,
            options: options,
            baseDataTestId: baseDataTestId,
            dataTestId: dataTestId,
            dropdownStyles: dropdownStyles,
            focusableRefs: focusableRefs,
            tabbingAccessibility: tabbingAccessibility
          }) : _jsx(MenuList, _objectSpread(_objectSpread({
            ref: isCombobox ? el => el === null || el === void 0 ? void 0 : el.setAttribute('role', 'listbox') : undefined,
            css: {
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                width: '8px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--chakra-colors-lightGrey3)',
                borderRadius: '24px'
              }
            },
            "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-entireList"))
          }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuListStyles), {}, {
            children: _jsx(MenuGroup, {
              "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-entireListGroup")),
              w: "full",
              children: options.map((option, index) => _jsx(MenuItem, _objectSpread(_objectSpread({
                ref: isCombobox ? el => {
                  if (!el) return;
                  el.setAttribute('role', 'option');
                  el.setAttribute('aria-selected', String(option.id) === String(selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.id) ? 'true' : 'false');
                } : undefined,
                "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-").concat(index)),
                value: option.id,
                type: "button"
              }, dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuItemStyles), {}, {
                onClick: () => {
                  var _props$onChange;
                  setIsOpen(false);
                  if (option.id === (selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.id)) return;
                  (_props$onChange = props.onChange) === null || _props$onChange === void 0 || _props$onChange.call(props, option);
                  setSelectedValue(option);
                  onBlur === null || onBlur === void 0 || onBlur();
                },
                children: renderDropdownOption(option)
              }), option.id))
            })
          }))]
        }))]
      }))
    }));
  }
  function getLabelColor(isDropdownOpen) {
    var color = labelColorMap[variant];
    if (isDropdownOpen) {
      color = labelColorMap["".concat(variant, "Open")];
    }
    if (props.disabled) {
      color = labelColorMap["".concat(variant, "Disabled")];
    }
    return color;
  }
  function renderDropdownOption(option) {
    var buttonSelector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var typographyStyles = buttonSelector ? dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuButtonTextStyles : dropdownStyles === null || dropdownStyles === void 0 ? void 0 : dropdownStyles.menuItemTextStyles;
    return _jsxs(_Fragment, {
      children: [(option === null || option === void 0 ? void 0 : option.icon) && _jsx(Box, {
        pr: "md",
        children: option.icon
      }), buttonSelector ? _jsx(Box, {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "data-testid": formatDataTestId(baseDataTestId, "".concat(dataTestId, "-buttonSelectorLabel")),
        children: _jsx(Text, _objectSpread(_objectSpread({
          as: "span"
        }, typographyStyles), {}, {
          children: option === null || option === void 0 ? void 0 : option.label
        }))
      }) : _jsx(Text, _objectSpread(_objectSpread({
        as: "span"
      }, typographyStyles), {}, {
        children: option === null || option === void 0 ? void 0 : option.label
      }))]
    });
  }
  function getOptionById(option) {
    return options.find(o => String(o.id) === option);
  }
}
var labelStyles = {
  top: '-7px',
  left: 'var(--chakra-space-xmd)',
  bgColor: 'var(--chakra-colors-baseWhite)',
  fontSize: 'var(--chakra-fontSizes-sm)',
  lineHeight: 'var(--chakra-lineHeights-1)',
  pr: 'xs',
  pl: 'xs',
  m: '0',
  _hover: {
    color: 'darkGrey2'
  }
};
var labelColorMap = {
  default: 'var(chakra-colors-darkGrey1)',
  defaultOpen: 'var(--chakra-colors-primary)',
  defaultDisabled: 'var(--chakra-colors-lightGrey2)',
  error: 'var(--chakra-colors-error)',
  errorOpen: 'var(--chakra-colors-error)',
  errorDisabled: 'var(--chakra-colors-error)'
};
var placeholderStyle = {
  w: '100%',
  textOverflow: 'ellipsis',
  overflow: 'hidden'
};
var errorIconStyle = {
  ml: 'md',
  alignSelf: 'center'
};
var dropdownWithStatusIconWrapperStyle = {
  align: 'center',
  w: 'full'
};
export default memo(Dropdown);